// @file Composable to show snackbar with smart auto-dismiss behavior.
// Snackbar persists until BOTH: tab is visible AND user interacts, then auto-dismisses.
import { GlobalSnackbarNotification, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useDocumentVisibility, useEventListener } from '@vueuse/core'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

const DEFAULT_AUTO_DISMISS_TIMEOUT_MS = 5000

function useAttentionAwareSnackbar() {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const activeSnackbarUid = ref<string | null>(null)

  let unwatchConditions: (() => void) | null = null
  let cleanupInteractionListeners: (() => void) | null = null

  const visibility = useDocumentVisibility()
  const isTabVisible = computed(() => visibility.value === 'visible')

  function showAttentionAwareSnackbar(options: GlobalSnackbarNotification): string {
    cleanup()

    const hasUserInteracted = ref(false)

    // First, show a persistent, non-dismissable snackbar.
    activeSnackbarUid.value = globalSnackbarStore.setSnackbar({
      ...options,
      persist: true,
      dismissable: false,
    })

    // Set up interaction detection handler
    const handleInteraction = () => {
      if (hasUserInteracted.value) return // Already handled
      hasUserInteracted.value = true
      cleanupInteractionListeners?.()
      cleanupInteractionListeners = null
    }

    // Set up event listeners for user interactions using VueUse's useEventListener
    // These will be automatically cleaned up when the component unmounts
    const cleanup1 = useEventListener(document, 'click', handleInteraction, { once: true, passive: true })
    const cleanup2 = useEventListener(document, 'touchstart', handleInteraction, { once: true, passive: true })
    const cleanup3 = useEventListener(document, 'keydown', handleInteraction, { once: true })
    const cleanup4 = useEventListener(document, 'scroll', handleInteraction, { once: true, passive: true })

    // Store cleanup function to manually cleanup if needed before unmount
    cleanupInteractionListeners = () => {
      cleanup1()
      cleanup2()
      cleanup3()
      cleanup4()
    }

    unwatchConditions = watch(
      [isTabVisible, hasUserInteracted],
      ([visible, interacted]) => {
        if (visible && interacted && activeSnackbarUid.value) {
          // BOTH conditions met: update snackbar to auto-dismiss.
          // Only update the specific behavior fields we want to change
          globalSnackbarStore.updateSnackbar(activeSnackbarUid.value, {
            message: options.message, // Required field, keep original value
            persist: false,
            dismissable: true, // Allow user to dismiss manually
            timeout: options.timeout ?? DEFAULT_AUTO_DISMISS_TIMEOUT_MS,
          })
          unwatchConditions?.()
          unwatchConditions = null
          cleanupInteractionListeners?.()
          cleanupInteractionListeners = null
        }
      },
      { immediate: true },
    )

    return activeSnackbarUid.value
  }

  function cleanup(): void {
    unwatchConditions?.()
    unwatchConditions = null
    cleanupInteractionListeners?.()
    cleanupInteractionListeners = null
    if (activeSnackbarUid.value && globalSnackbarStore.isSnackbarBeingShown(activeSnackbarUid.value)) {
      globalSnackbarStore.removeSnackbar(activeSnackbarUid.value)
    }
    activeSnackbarUid.value = null
  }

  onBeforeUnmount(cleanup)

  return { showAttentionAwareSnackbar, cleanup }
}

export { useAttentionAwareSnackbar }
